import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setMobileCatOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  return (
    <nav className="fixed top-0 left-0 w-full z-50 bg-background-50/90 backdrop-blur-md border-b border-background-200/60">
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="text-xl md:text-2xl font-heading font-bold text-foreground-950 whitespace-nowrap"
          >
            superbuy.courses
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                isHome ? 'text-primary-600' : 'text-foreground-700 hover:text-foreground-950'
              }`}
            >
              <i className="ri-home-4-line w-3.5 h-3.5 flex items-center justify-center inline mr-1.5"></i>
              Home
            </Link>

            <Link
              to="/what-is-superbuy-spreadsheet/"
              className="text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors whitespace-nowrap"
            >
              <i className="ri-book-open-line w-3.5 h-3.5 flex items-center justify-center inline mr-1.5"></i>
              Shopping Guide
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap bg-foreground-950 text-background-50 hover:bg-foreground-800 cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 right-0 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg py-3 max-h-96 overflow-y-auto z-50">
                  <p className="px-4 pb-2 text-xs font-semibold uppercase tracking-wider text-foreground-500">Browse Collections</p>
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-8 h-8 rounded-lg bg-background-200 overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
              Buy It Now
              <i className="ri-arrow-right-up-line w-3.5 h-3.5 flex items-center justify-center"></i>
            </a>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-950"
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/60 py-4 px-2 -mx-4 max-h-[75vh] overflow-y-auto">
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${
                  isHome ? 'bg-primary-50 text-primary-700' : 'text-foreground-700 hover:bg-background-100'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center">
                  <i className="ri-home-4-line text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
                </div>
                Home
              </Link>

              <Link
                to="/what-is-superbuy-spreadsheet/"
                className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-foreground-700 hover:bg-background-100 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center">
                  <i className="ri-book-open-line text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
                </div>
                Shopping Guide
              </Link>

              <div className="border-t border-background-200/60 my-2"></div>

              <button
                onClick={() => setMobileCatOpen(!mobileCatOpen)}
                className="flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-semibold text-foreground-950 hover:bg-background-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-background-200 flex items-center justify-center">
                    <i className="ri-grid-line text-foreground-700 w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  Categories
                </div>
                <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {mobileCatOpen && (
                <div className="grid grid-cols-2 gap-2 px-2 pb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-xs text-foreground-700 hover:bg-background-100 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <div className="w-7 h-7 rounded-md bg-background-200 overflow-hidden flex-shrink-0">
                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                      </div>
                      <span className="whitespace-nowrap text-xs leading-tight">{cat.name}</span>
                    </a>
                  ))}
                </div>
              )}

              <div className="border-t border-background-200/60 my-2"></div>

              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 mx-4 px-5 py-3.5 bg-primary-500 text-white rounded-full text-sm font-semibold hover:bg-primary-600 transition-all cursor-pointer"
                onClick={() => setMobileMenuOpen(false)}
              >
                <i className="ri-shopping-bag-line w-4 h-4 flex items-center justify-center"></i>
                Buy It Now
                <i className="ri-arrow-right-up-line w-3.5 h-3.5 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}